import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import HorizontalScrollContainer from "common/components/HorizontalScrollContainer";
import { useTranslation } from "common/locales";
import type { Feature, FeatureCollection } from "geojson";
import {
  getArableFieldMetricsFromArray,
  getArableFieldsFilteredFromArray,
  getGrainFieldMetricsFromArray,
  getGrainFieldsFromArray,
  getSilageFieldMetricsFromArray,
  getSilageFieldsFromArray,
  useArableFields,
  useFields,
} from "model";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUIStore } from "stores";
import { AnalyticsEvents, mixpanel } from "../../common/analytics";
import FieldMapPreview from "./FieldMapPreview";

const FieldCard = styled(Card)({
  minWidth: 280,
  maxWidth: 320,
  cursor: "pointer",
  transition: "transform 0.2s",
  backgroundColor: "white",
  borderRadius: 16,
  "&:hover": {
    transform: "translateY(-2px)",
  },
});

const MapPreviewContainer = styled("div")({
  height: 120,
  background: "#f5f5f5",
  borderRadius: "16px 16px 0 0",
  position: "relative",
  overflow: "hidden",
  margin: "-16px -16px 16px -16px",
});

const MapPlaceholderText = styled(Typography)({
  fontWeight: 500,
  letterSpacing: "0.5px",
  opacity: 0.8,
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
});

const MetricsContainer = styled("div")({
  padding: "8px 0px",
});

const AddMoreButton = styled(Button)({
  color: "#2e7831",
  textTransform: "none",
});

interface MetricItemProps {
  label: string;
  value: string | number;
  unit?: string;
}

function MetricItem({ label, value, unit }: MetricItemProps) {
  return (
    <Tooltip title={String(value)} enterDelay={500} enterNextDelay={500}>
      <Box
        sx={{
          py: 0.75,
          borderBottom: "1px solid #F0F0F0",
          "&:last-child": {
            borderBottom: "none",
          },
          minWidth: 0,
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", display: "block", fontSize: "0.7rem" }}
        >
          {label}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.5,
            minWidth: 0,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: "#1a1a2e",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
          {unit && (
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "0.7rem",
                flexShrink: 0,
              }}
            >
              {unit}
            </Typography>
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

interface BaseField {
  guid: string;
  geom: Feature;
}

interface FieldMetrics {
  fieldCount: number;
  totalArea: number;
  totalDryMatter?: number;
  growthRate?: number;
  totalCover?: number;
}

interface FieldTypeCardProps {
  title: string;
  fields: BaseField[];
  metrics: FieldMetrics;
  onCardClick: () => void;
}

const FieldTypeCard: React.FC<FieldTypeCardProps> = ({
  title,
  fields,
  metrics,
  onCardClick,
}) => {
  const { t } = useTranslation();

  const fieldGeojson: FeatureCollection = {
    type: "FeatureCollection",
    features: fields.map((field) => field.geom),
  };

  const isArable =
    title === t("fieldTypeCards.grain.title") ||
    title === t("fieldTypeCards.arable.title");

  const isSilage = title === t("fieldTypeCards.silage.title");

  // Convert dry matter to tonnes for silage fields
  const dryMatterTonnes = metrics.totalDryMatter
    ? (metrics.totalDryMatter / 1000).toFixed(1)
    : "0";

  return (
    <FieldCard onClick={onCardClick}>
      <CardContent>
        <MapPreviewContainer>
          {fields.length > 0 ? (
            <FieldMapPreview
              geojson={fieldGeojson}
              isArable={isArable}
              fieldIds={fields.map((field) => field.guid)}
            />
          ) : (
            <MapPlaceholderText variant="body2">No Fields</MapPlaceholderText>
          )}
        </MapPreviewContainer>
        <Typography variant="body1" fontWeight="medium" sx={{ mb: 1 }}>
          {title}
        </Typography>
        <MetricsContainer>
          <MetricItem
            label={t("fieldTypeCards.metrics.fields")}
            value={metrics.fieldCount.toLocaleString()}
          />
          <MetricItem
            label={t("fieldTypeCards.metrics.hectares")}
            value={metrics.totalArea.toLocaleString()}
            unit="Ha"
          />
          {isSilage && metrics.totalDryMatter !== undefined && (
            <MetricItem
              label={t("fieldTypeCards.metrics.dryMatterAvailable")}
              value={dryMatterTonnes}
              unit={t("farm.availableDM.unit")}
            />
          )}
        </MetricsContainer>
      </CardContent>
    </FieldCard>
  );
};

function FieldTypeCardSkeleton() {
  return (
    <FieldCard sx={{ pointerEvents: "none" }}>
      <CardContent>
        <MapPreviewContainer>
          <Skeleton variant="rectangular" height="100%" width="100%" />
        </MapPreviewContainer>

        <Skeleton variant="text" width={100} height={24} sx={{ mb: 1 }} />

        <MetricsContainer>
          <Box sx={{ py: 0.75, borderBottom: "1px solid #F0F0F0" }}>
            <Skeleton variant="text" width={40} height={14} />
            <Skeleton variant="text" width={30} height={20} />
          </Box>
          <Box sx={{ py: 0.75 }}>
            <Skeleton variant="text" width={60} height={14} />
            <Skeleton variant="text" width={40} height={20} />
          </Box>
        </MetricsContainer>
      </CardContent>
    </FieldCard>
  );
}

export function FieldTypeCardsSkeleton() {
  return (
    <Box>
      <HorizontalScrollContainer>
        {[1, 2, 3].map((i) => (
          <FieldTypeCardSkeleton key={i} />
        ))}
      </HorizontalScrollContainer>
    </Box>
  );
}

export default function FieldTypeCards() {
  const setIsEditing = useUIStore((state) => state.setIsEditing);
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Use React Query hooks for data fetching
  const { data: fields = [], isLoading: fieldsLoading } = useFields();
  const { data: allArableFields = [], isLoading: arableFieldsLoading } =
    useArableFields();

  // Compute derived data using helper functions
  const grainFields = useMemo(
    () => getGrainFieldsFromArray(allArableFields),
    [allArableFields]
  );
  const arableFields = useMemo(
    () => getArableFieldsFilteredFromArray(allArableFields),
    [allArableFields]
  );
  const silageFields = useMemo(
    () => getSilageFieldsFromArray(fields),
    [fields]
  );

  const grainMetrics = useMemo(
    () => getGrainFieldMetricsFromArray(grainFields),
    [grainFields]
  );
  const arableMetrics = useMemo(
    () => getArableFieldMetricsFromArray(arableFields),
    [arableFields]
  );
  const silageMetrics = useMemo(
    () => getSilageFieldMetricsFromArray(silageFields),
    [silageFields]
  );

  const createFieldGeoms = (fields: BaseField[]): FeatureCollection | null => {
    if (!fields || fields.length === 0) return null;

    const features = fields.map((field) => ({
      ...field.geom,
      properties: {
        ...field.geom.properties,
        id: field.guid,
      },
    }));

    return {
      type: "FeatureCollection",
      features: features,
    } as FeatureCollection;
  };

  const handleCardClick = (fields: BaseField[], title: string) => {
    const geoms = createFieldGeoms(fields);
    if (geoms && geoms.features.length > 0) {
      mixpanel.track(AnalyticsEvents.FIELD_TYPE_CARD_CLICKED, {
        fieldType: title,
      });
      const fieldTypeKey =
        title === t("fieldTypeCards.grain.title")
          ? "grain"
          : title === t("fieldTypeCards.arable.title")
          ? "arable"
          : title === t("fieldTypeCards.silage.title")
          ? "silage"
          : "";
      navigate(`/fields-list/${fieldTypeKey}`);
    }
  };

  const handleAddMoreFields = () => {
    mixpanel.track(AnalyticsEvents.ADD_MORE_FIELDS_CLICKED);
    setIsEditing(true);
    navigate(`/add-field/enter-details`);
  };

  const fieldTypes = [
    {
      title: t("fieldTypeCards.grain.title"),
      fields: grainFields.filter(Boolean) as BaseField[],
      metrics: grainMetrics,
    },
    {
      title: t("fieldTypeCards.arable.title"),
      fields: arableFields.filter(Boolean) as BaseField[],
      metrics: arableMetrics,
    },
    {
      title: t("fieldTypeCards.silage.title"),
      fields: silageFields.filter(Boolean) as BaseField[],
      metrics: silageMetrics,
    },
  ].filter((fieldType) => fieldType.fields.length > 0);

  const isLoading =
    (fieldsLoading || arableFieldsLoading) && fieldTypes.length === 0;

  if (isLoading) {
    return <FieldTypeCardsSkeleton />;
  }

  return (
    <Box>
      <HorizontalScrollContainer>
        {fieldTypes.map((fieldType) => (
          <FieldTypeCard
            key={fieldType.title}
            title={fieldType.title}
            fields={fieldType.fields}
            metrics={fieldType.metrics}
            onCardClick={() =>
              handleCardClick(fieldType.fields, fieldType.title)
            }
          />
        ))}
      </HorizontalScrollContainer>
    </Box>
  );
}
